// src/components/admin/adminUi.js
// Admin 域共享 UI 组件库：统一的工作台视觉语言。
// 约定：admin 主强调色用 --color-warning（琥珀），与主站 accent（橙）区分；
// 面板一律实色 bg-secondary + 1px hairline 边框，无玻璃拟态、无 glow。
import { AppSelect } from '../ui/AppSelect';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { displayUserId } from "./adminUtils";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";

export const ADMIN_ACCENT = "var(--color-warning)";

// ============================
// 面板与排版
// ============================

export const Panel = ({ title, sub, right, children, className = "" }) => (
  <section
    className={`rounded-lg border ${className}`}
    style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}
  >
    {(title || right) && (
      <header className="flex items-baseline justify-between gap-3 px-4 pt-3.5 pb-3 border-b" style={{ borderColor: "var(--color-border-secondary)" }}>
        <div className="flex items-baseline gap-2.5 min-w-0">
          {title && <h3 className="text-[13px] font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</h3>}
          {sub && <span className="text-label truncate">{sub}</span>}
        </div>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const StatCard = ({ label, value, color }) => (
  <div className="rounded-lg px-4 py-3" style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-secondary)" }}>
    <div className="text-label">{label}</div>
    <div className="font-mono-num text-xl leading-7 font-semibold mt-1 truncate" style={color ? { color } : undefined}>{value}</div>
  </div>
);

export const EmptyBlock = ({ text, hint }) => (
  <div
    className="rounded-lg border border-dashed py-10 flex flex-col items-center justify-center gap-1.5"
    style={{ borderColor: "var(--color-border-primary)" }}
  >
    <div className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>{text}</div>
    {hint && <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>{hint}</div>}
  </div>
);

export const EmptyText = ({ text }) => (
  <div className="py-10 text-center text-xs" style={{ color: "var(--color-text-muted)" }}>{text}</div>
);

export const ErrorNotice = ({ message }) => (
  <div className="rounded-lg px-3.5 py-2.5 text-sm mb-4"
    style={{ backgroundColor: "var(--color-red-light)", border: "1px solid var(--color-error)", color: "var(--color-error)" }}>
    {message}
  </div>
);

// ============================
// 控件
// ============================

export const Segmented = ({ options, value, onChange, ariaLabel }) => (
  <div
    className="inline-flex items-center gap-1 rounded-lg p-1 border shrink-0"
    role="group"
    aria-label={ariaLabel}
    style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
  >
    {options.map((opt) => {
      const active = value === opt.key;
      return (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className="px-3 h-7 rounded-md text-xs font-medium transition-colors duration-150 cursor-pointer"
          style={active
            ? { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)" }
            : { color: "var(--color-text-muted)" }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

export const FilterInput = ({ label, value, onChange, type = 'text', placeholder, ...inputProps }) => (
  <div>
    <label className="text-label block mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      {...inputProps}
      className="h-8 px-2.5 rounded-md text-xs outline-none"
      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
    />
  </div>
);

export const FilterSelect = ({ label, value, options, onChange }) => (
  <div>
    <label className="text-label block mb-1.5">{label}</label>
    <AppSelect
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 px-2 rounded-md text-xs outline-none"
      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
    >
      {options.map(o => <option key={o} value={o}>{o || '(全部)'}</option>)}
    </AppSelect>
  </div>
);

// 按钮体系：主操作（琥珀）/ 次操作 / 危险操作 / 图标按钮
export const PrimaryButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`h-8 px-3.5 rounded-md text-xs font-semibold cursor-pointer disabled:opacity-50 transition-colors duration-150 ${className}`}
    style={{ backgroundColor: ADMIN_ACCENT, color: "var(--color-bg-primary)" }}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`h-8 px-3.5 rounded-md text-xs font-medium border cursor-pointer disabled:opacity-50 transition-colors duration-150 ${className}`}
    style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}
  >
    {children}
  </button>
);

export const DangerButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`h-8 px-3.5 rounded-md text-xs font-medium border cursor-pointer disabled:opacity-50 transition-colors duration-150 ${className}`}
    style={{ borderColor: "rgba(239,68,68,0.3)", color: "var(--color-error)", backgroundColor: "var(--color-red-light)" }}
  >
    {children}
  </button>
);

export const IconAction = ({ title, onClick, icon, danger = false }) => {
  const Icon = icon;
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className="w-8 h-8 inline-flex items-center justify-center rounded-md transition-colors duration-150 cursor-pointer"
      style={{
        backgroundColor: danger ? 'var(--color-red-light)' : 'var(--color-bg-tertiary)',
        color: danger ? 'var(--color-error)' : 'var(--color-text-secondary)',
        border: `1px solid ${danger ? 'rgba(239,68,68,0.3)' : 'var(--color-border-primary)'}`,
      }}
    >
      <Icon size={14} />
    </button>
  );
};

// ============================
// 表格与数据展示
// ============================

export const TaskTable = ({ rows, columns }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-label" style={{ textAlign: "left" }}>
          {columns.map(c => <th key={c.key} className="font-medium pb-2.5 pr-4">{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length}><EmptyText text="暂无数据" /></td></tr>
        ) : rows.map((r, i) => (
          <tr key={i} className="hover:bg-[var(--color-bg-tertiary)] transition-colors" style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
            {columns.map(c => (
              <td key={c.key} className={`py-2 pr-4 text-xs ${c.className || ''}`}>
                {c.render ? c.render(r[c.key], r) : (c.key === 'userId' ? displayUserId(r[c.key]) : (r[c.key] != null ? String(r[c.key]) : '-'))}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const DetailBlock = ({ title, value }) => (
  <details className="rounded-md p-2.5" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }} open={title === '错误' && !!value}>
    <summary className="cursor-pointer text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>{title}</summary>
    <pre className="animate-in fade-in duration-150 whitespace-pre-wrap break-words max-h-72 overflow-auto text-2xs font-mono-num" style={{ color: value ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
      {value || '-'}
    </pre>
  </details>
);

export const TraceList = ({ title, rows, render }) => (
  <details className="rounded-md p-2.5" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
    <summary className="cursor-pointer text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>{title} ({rows?.length || 0})</summary>
    <div className="animate-in fade-in duration-150 space-y-1 max-h-56 overflow-auto custom-scrollbar">
      {!(rows || []).length ? (
        <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无记录</div>
      ) : rows.map((row, idx) => (
        <div key={row.id || idx} className="font-mono-num text-2xs break-all rounded px-2 py-1" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-muted)' }}>
          {render(row)}
        </div>
      ))}
    </div>
  </details>
);

// ============================
// 徽章
// ============================

const TYPE_META = {
  image: { label: "图片", color: "var(--color-blue-primary)" },
  video: { label: "视频", color: "var(--color-purple-primary)" },
  text: { label: "文本", color: "var(--color-green-primary)" },
  audio: { label: "音频", color: "var(--color-teal-primary)" },
};

export const TypeBadge = ({ type }) => {
  const meta = TYPE_META[type];
  if (!meta) return <span style={{ color: "var(--color-text-muted)" }}>{type || "-"}</span>;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium"
      style={{ color: meta.color, backgroundColor: `color-mix(in srgb, ${meta.color} 12%, transparent)` }}
    >
      {meta.label}
    </span>
  );
};

// 通用状态徽章：statusColorMap 不含时回退 muted
export const StatusBadge = ({ label, color }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded-md text-2xs font-medium"
    style={{
      color: color || "var(--color-text-muted)",
      backgroundColor: `color-mix(in srgb, ${color || "var(--color-text-muted)"} 12%, transparent)`,
    }}
  >
    {label}
  </span>
);

// ============================
// 可搜索项目选择器
// ============================

export const ProjectCombobox = ({
  value,
  onChange,
  projectOptions = [],
  placeholder = "全部项目",
  maxWidth = 280,
  onSearch,
  searching = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const blurTimer = useRef(null);
  const menu = useDelayedUnmount(open, 150);

  useEffect(() => {
    if (!open || typeof onSearch !== "function") return undefined;
    const timer = setTimeout(() => onSearch(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [open, onSearch, query]);

  const selected = useMemo(
    () => projectOptions.find(p => String(p.id) === String(value)),
    [projectOptions, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projectOptions.slice(0, 100);
    return projectOptions.filter(p => {
      const name = String(p.name || "").toLowerCase();
      const id = String(p.id ?? "");
      const owner = displayUserId(p.userId, "").toLowerCase();
      return name.includes(q) || id.includes(q) || owner.includes(q);
    }).slice(0, 100);
  }, [projectOptions, query]);

  const displayValue = open ? query : (selected ? `${selected.name || '未命名'}` : "");
  const hasValue = value != null && value !== "";

  const commit = (next) => {
    onChange(next == null ? "" : String(next));
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative" style={{ minWidth: 200, maxWidth }}>
      <input
        value={displayValue}
        placeholder={placeholder}
        onFocus={() => { clearTimeout(blurTimer.current); setQuery(""); setOpen(true); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onBlur={() => { blurTimer.current = setTimeout(() => { setOpen(false); setQuery(""); }, 120); }}
        onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); setQuery(""); e.target.blur(); } }}
        className="h-8 w-full rounded-md pl-2.5 pr-8 text-xs outline-none"
        style={{ backgroundColor: 'var(--color-bg-tertiary)', border: `1px solid ${open ? 'var(--color-border-hover)' : 'var(--color-border-primary)'}`, color: 'var(--color-text-primary)' }}
      />
      {hasValue && !open && (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); commit(""); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-xs"
          style={{ color: 'var(--color-text-muted)' }}
          title="清除项目筛选"
        >
          ✕
        </button>
      )}
      {menu.mounted && (
        <div
          className={`absolute left-0 top-full mt-1 z-40 w-full min-w-[240px] rounded-md border overflow-hidden ${menu.exiting ? "animate-out fade-out slide-out-to-bottom-2 duration-150" : "animate-in fade-in slide-in-from-bottom-2 duration-300"}`}
          style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)', boxShadow: 'var(--shadow-dropdown)' }}
        >
          <div className="max-h-64 overflow-y-auto custom-scrollbar py-1">
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(""); }}
              className="w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors duration-100"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {placeholder}
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {searching ? '正在搜索...' : '没有匹配的项目'}
              </div>
            ) : filtered.map(p => {
              const isActive = String(p.id) === String(value);
              return (
                <button
                  key={p.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); commit(p.id); }}
                  className={`w-full text-left px-3 py-1.5 cursor-pointer transition-colors duration-100 ${isActive ? '' : 'hover:bg-[var(--color-bg-tertiary)]'}`}
                  style={isActive ? {
                    backgroundColor: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
                    boxShadow: 'inset 2px 0 0 var(--color-warning)',
                  } : undefined}
                >
                  <div className="text-xs font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{p.name || '未命名'}</div>
                  <div className="text-2xs font-mono-num truncate mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                    #{p.id} · {displayUserId(p.userId)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================
// 筛选条（项目 + 用户ID 范围）
// ============================

export const AdminScopeFilterBar = ({ scope, setScope, projectOptions = [], onRefresh, loading, children }) => (
  <div className="rounded-lg px-4 py-3 flex flex-wrap gap-3 items-end mb-4"
    style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}>
    <div>
      <label className="text-label block mb-1.5">项目</label>
      <ProjectCombobox
        value={scope.projectId || ''}
        onChange={(v) => setScope(prev => ({ ...prev, projectId: v }))}
        projectOptions={projectOptions}
      />
    </div>
    <FilterInput label="用户ID" value={scope.userId || ''} onChange={(v) => setScope(prev => ({ ...prev, userId: v }))} />
    {children}
    {onRefresh && (
      <PrimaryButton onClick={onRefresh} disabled={loading}>
        {loading ? '查询中…' : '应用筛选'}
      </PrimaryButton>
    )}
    {(scope.projectId || scope.userId) && (
      <SecondaryButton onClick={() => setScope({ projectId: '', userId: '' })}>
        清空范围
      </SecondaryButton>
    )}
  </div>
);
